import {Divider, Group, Paper, Stack, Table, Text, Title} from '@mantine/core';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FieldPath, SubmitHandler, useFormContext} from 'react-hook-form';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {Error, PartItemDetailDto} from '../../api/generated/index.defs';
import {JobsService} from '../../api/generated/JobsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {ROUTES} from '../../routes';
import {useJobStore} from '../job-store';
import {jobReplacedPartsSchema, JobReplacedPartsSchema} from '../schemas';
import classes from './job-archive.module.css';
import {JobTypes, Roles} from '../../api/generated/enums';
import {useRequireAuthorization} from '../../auth/require-authorization';

const Form = createForm<JobReplacedPartsSchema>();

export const JobArchiveReplacedParts: React.FC = () => {
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const [fieldErrors, setFieldErrors] = useState<Error[]>([]);

  const {
    fetchJob,
    fetchReplacedParts,
    isJobReadonly,
    replacedPartItems,
    isLoadingReplacedParts,
    job,
    isLoading,
  } = useJobStore((state) => ({
    fetchJob: state.fetchJob,
    fetchReplacedParts: state.fetchReplacedParts,
    isJobReadonly: state.isJobReadonly,
    isLoadingReplacedParts: state.isLoadingReplacedParts,
    replacedPartItems: state.replacedPartItems,
    job: state.job,
    isLoading: state.isJobLoading,
  }));

  const defaultValues = useMemo(
    () => ({partItems: replacedPartItems ?? []}),
    [replacedPartItems],
  );

  const onSubmit = useCallback<SubmitHandler<JobReplacedPartsSchema>>(
    async (value) => {
      setFieldErrors([]);
      const response = await JobsService.updateReplacedParts({
        jobId: id,
        body: value,
      });

      if (response.hasErrors && response.errors) {
        setFieldErrors(response.errors);
      } else {
        notifications.success('Replaced parts updated');
        fetchReplacedParts(id);
      }

      fetchJob(id);
    },
    [fetchJob, fetchReplacedParts, id, notifications],
  );

  if (!isLoading && job?.jobType === JobTypes['Non-M&M']) return null;

  return (
    <div>
      <Title fz='sm' className={classes.title}>
        Replaced Parts
      </Title>
      <Paper withBorder shadow='sm' radius='0 0 6px 6px'>
        <AsyncStateContainer loading={isLoadingReplacedParts}>
          <Form
            onSubmit={onSubmit}
            schema={jobReplacedPartsSchema}
            defaultValues={defaultValues}
          >
            {({formState: {isSubmitting}}) => (
              <>
                <Table withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th fw='normal'>Part Number</Table.Th>
                      <Table.Th fw='normal'>Description</Table.Th>
                      <Table.Th fw='normal'>Seq</Table.Th>
                      <Table.Th fw='normal'>Qty</Table.Th>
                      <Table.Th fw='normal'>Lot Number</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {replacedPartItems && replacedPartItems.length > 0 && (
                      <ReplacedPartsTableRows
                        isReadonly={isJobReadonly}
                        fieldErrors={fieldErrors}
                        partItems={replacedPartItems}
                      />
                    )}
                  </Table.Tbody>
                </Table>
                <Divider />
                <Group p={16} justify='flex-end'>
                  <Form.Submit loading={isSubmitting} disabled={isJobReadonly}>
                    Save Replaced Parts
                  </Form.Submit>
                </Group>
              </>
            )}
          </Form>
        </AsyncStateContainer>
      </Paper>
    </div>
  );
};

const LotNumberOverrideRoles = ['Global Admin'] as Roles[];

const ReplacedPartsTableRows = ({
  partItems,
  isReadonly,
  fieldErrors,
}: {
  partItems: PartItemDetailDto[];
  fieldErrors: Error[];
  isReadonly: boolean;
}) => {
  const isOverrideAuthorized = useRequireAuthorization({
    role: LotNumberOverrideRoles,
  });

  const {setError} = useFormContext<JobReplacedPartsSchema>();

  useEffect(() => {
    if (fieldErrors.length > 0) {
      fieldErrors.forEach((error) => {
        setError(error.propertyName as FieldPath<JobReplacedPartsSchema>, {
          type: 'manual',
          message: error.errorMessage,
        });
      });
    }
  }, [fieldErrors, setError]);

  return (
    <Form.FieldArray name='partItems'>
      {() =>
        partItems.map((partItem, partItemIndex) => (
          <Table.Tr key={partItem.id}>
            <Table.Td>{partItem.partNumber}</Table.Td>
            <Table.Td>{partItem.description}</Table.Td>
            <Table.Td>{partItem.materialSequence}</Table.Td>
            <Table.Td>{partItem.actionQty ?? partItem.qty}</Table.Td>
            <Table.Td>
              <Stack gap='xs'>
                {_.times(
                  partItem.actionQty ?? partItem.qty ?? 1,
                  (lotIndex) => (
                    <>
                      {!isOverrideAuthorized &&
                      partItem.lotNumbers?.[lotIndex]?.jobNumber ? (
                        <Text size='sm' pl={12}>
                          {partItem.lotNumbers?.[lotIndex]?.value || 'N/A'}
                        </Text>
                      ) : (
                        <Form.TextInput
                          key={lotIndex}
                          name={`partItems.${partItemIndex}.lotNumbers.${lotIndex}.value`}
                          disabled={isReadonly}
                        />
                      )}
                    </>
                  ),
                )}
              </Stack>
            </Table.Td>
          </Table.Tr>
        ))
      }
    </Form.FieldArray>
  );
};